import { useMemo, useRef, useCallback } from 'react';
import {
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import type { PlaybookStep } from '@chatsdet/types';
import type { WorkflowNode } from '../../../../types/workflow';

/** Generate a short random ID for internal tracking (not persisted). */
function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export interface FlatPlaybookItem {
  id: string;
  nodeId: string;
  step: PlaybookStep;
}

interface UsePlaybookDragDropOptions {
  nodes: WorkflowNode[];
  onUpdateNode: (nodeId: string, data: Record<string, unknown>) => Promise<void>;
}

export function usePlaybookDragDrop({ nodes, onUpdateNode }: UsePlaybookDragDropOptions) {
  const internalIdsRef = useRef<string[]>([]);

  const flatItems = useMemo((): FlatPlaybookItem[] => {
    const items: FlatPlaybookItem[] = [];
    for (const node of nodes) {
      const data = (node.data ?? {}) as Record<string, unknown>;
      const steps = (data.playbookSteps as PlaybookStep[] | undefined) ?? [];
      for (const step of steps) {
        items.push({ id: '', nodeId: node.id, step });
      }
    }

    // Sync internal IDs
    while (internalIdsRef.current.length < items.length) {
      internalIdsRef.current.push(generateId());
    }
    if (internalIdsRef.current.length > items.length) {
      internalIdsRef.current = internalIdsRef.current.slice(0, items.length);
    }

    return items.map((item, i) => ({ ...item, id: internalIdsRef.current[i] }));
  }, [nodes]);

  const sortableIds = internalIdsRef.current.slice(0, flatItems.length);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const ids = internalIdsRef.current;
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      // Reorder IDs
      const newIds = arrayMove([...ids], oldIndex, newIndex);
      internalIdsRef.current = newIds;

      // Reorder flat items
      const reordered = arrayMove([...flatItems], oldIndex, newIndex);

      // Only the moved item gets reassigned — inherit nodeId from its new neighbor above
      // (or keep its own if dropped at position 0)
      if (newIndex > 0) {
        reordered[newIndex] = { ...reordered[newIndex], nodeId: reordered[newIndex - 1].nodeId };
      }

      // Group by nodeId, reindex order (1-based), clear actionRefs
      const grouped = new Map<string, PlaybookStep[]>();
      for (const item of reordered) {
        const existing = grouped.get(item.nodeId) ?? [];
        existing.push({
          ...item.step,
          order: existing.length + 1,
          actionRefs: undefined,
        });
        grouped.set(item.nodeId, existing);
      }

      // Determine which nodes changed and update them
      const updatePromises: Promise<void>[] = [];
      for (const node of nodes) {
        const data = (node.data ?? {}) as Record<string, unknown>;
        const currentSteps = (data.playbookSteps as PlaybookStep[] | undefined) ?? [];
        const newSteps = grouped.get(node.id);

        if (newSteps === undefined && currentSteps.length === 0) continue;

        const stepsChanged =
          !newSteps && currentSteps.length > 0
            ? true
            : newSteps && JSON.stringify(newSteps) !== JSON.stringify(currentSteps);

        if (stepsChanged) {
          updatePromises.push(
            onUpdateNode(node.id, {
              playbookSteps: newSteps ?? [],
              playbookStepsUpdatedAt: new Date().toISOString(),
            }),
          );
        }
      }

      await Promise.all(updatePromises);
    },
    [flatItems, nodes, onUpdateNode],
  );

  return { flatItems, sensors, handleDragEnd, sortableIds };
}
